import React, { useState, useMemo } from 'react';

const SPARKLINE_WIDTH = 100;
const SPARKLINE_HEIGHT = 24;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    overflowX: 'auto',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '16px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    borderBottom: '2px solid #e5e7eb',
    color: '#6b7280',
    fontWeight: 600,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '8px 12px',
    borderBottom: '1px solid #f3f4f6',
    color: '#374151',
    whiteSpace: 'nowrap',
  },
  sortArrow: {
    marginLeft: '4px',
    fontSize: '10px',
    color: '#9ca3af',
  },
};

function Sparkline({ values }) {
  if (!values || values.length < 2) {
    return (
      <svg width={SPARKLINE_WIDTH} height={SPARKLINE_HEIGHT} viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}>
        <line x1="0" y1={SPARKLINE_HEIGHT / 2} x2={SPARKLINE_WIDTH} y2={SPARKLINE_HEIGHT / 2} stroke="#d1d5db" strokeWidth="1" strokeDasharray="2 2" />
      </svg>
    );
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const paddingY = 2;

  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * SPARKLINE_WIDTH;
    const y = paddingY + ((max - v) / range) * (SPARKLINE_HEIGHT - paddingY * 2);
    return `${x},${y}`;
  });

  return (
    <svg width={SPARKLINE_WIDTH} height={SPARKLINE_HEIGHT} viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}>
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke="#2563eb"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function getSortArrow(sortKey, currentSort) {
  if (currentSort.key !== sortKey) return '';
  return currentSort.dir === 'asc' ? ' \u25B2' : ' \u25BC';
}

export default function SparklineGrid({ addresses, historyByAddress }) {
  const [sortConfig, setSortConfig] = useState({ key: 'state', dir: 'asc' });

  const rows = useMemo(() => {
    if (!addresses) return [];

    return addresses.map((addr) => {
      const history = historyByAddress && historyByAddress[addr.addressId];
      const pest365Values = history
        ? [...history]
            .sort((a, b) => new Date(a.timeOfScrape || a.scrapedAt || a.timestamp || 0) - new Date(b.timeOfScrape || b.scrapedAt || b.timestamp || 0))
            .map((h) => {
              // Handle nested pricing object from backend
              const raw = h.pricing ? h.pricing.pest365 : h.pest365;
              if (typeof raw === 'number') return raw;
              if (typeof raw === 'string') { const n = parseFloat(raw.replace(/[$,]/g, '')); return isNaN(n) ? null : n; }
              return null;
            })
            .filter((v) => v != null)
        : [];

      const first = pest365Values.length > 0 ? pest365Values[0] : null;
      const last = pest365Values.length > 0 ? pest365Values[pest365Values.length - 1] : null;
      const cumulativeChange = first && last && first !== 0 ? ((last - first) / first) * 100 : 0;

      return {
        addressId: addr.addressId,
        address: addr.address || addr.street || '',
        city: addr.city || '',
        state: addr.state || '',
        currentPest365: addr.currentPest365 || addr.pest365 || last,
        cumulativeChange,
        sparklineValues: pest365Values,
      };
    });
  }, [addresses, historyByAddress]);

  const sortedRows = useMemo(() => {
    const sorted = [...rows];
    sorted.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (aVal < bVal) return sortConfig.dir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.dir === 'asc' ? 1 : -1;

      // Secondary sort: state asc, then city asc
      if (sortConfig.key !== 'state') {
        if (a.state < b.state) return -1;
        if (a.state > b.state) return 1;
      }
      if (sortConfig.key !== 'city') {
        if (a.city < b.city) return -1;
        if (a.city > b.city) return 1;
      }
      return 0;
    });
    return sorted;
  }, [rows, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  if (!addresses || addresses.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Address Price Sparklines</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No address data available
        </div>
      </div>
    );
  }

  const columns = [
    { key: 'address', label: 'Address' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'currentPest365', label: 'Pest365' },
    { key: 'cumulativeChange', label: '% Change' },
    { key: null, label: 'Trend' },
  ];

  return (
    <div style={styles.card}>
      <div style={styles.title}>Address Price Sparklines</div>
      <table style={styles.table}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.label}
                style={{
                  ...styles.th,
                  cursor: col.key ? 'pointer' : 'default',
                }}
                onClick={() => col.key && handleSort(col.key)}
              >
                {col.label}
                {col.key && (
                  <span style={styles.sortArrow}>
                    {getSortArrow(col.key, sortConfig)}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, idx) => (
            <tr
              key={row.addressId || idx}
              style={{
                backgroundColor: idx % 2 === 0 ? '#fff' : '#f9fafb',
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#eff6ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#fff' : '#f9fafb';
              }}
            >
              <td style={{ ...styles.td, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {row.address}
              </td>
              <td style={styles.td}>{row.city}</td>
              <td style={styles.td}>{row.state}</td>
              <td style={styles.td}>
                {row.currentPest365 != null ? `$${row.currentPest365.toFixed(2)}` : '--'}
              </td>
              <td
                style={{
                  ...styles.td,
                  fontWeight: 600,
                  color:
                    row.cumulativeChange > 0
                      ? '#dc2626'
                      : row.cumulativeChange < 0
                      ? '#16a34a'
                      : '#6b7280',
                }}
              >
                {row.cumulativeChange !== 0
                  ? `${row.cumulativeChange > 0 ? '+' : ''}${row.cumulativeChange.toFixed(2)}%`
                  : '0.00%'}
              </td>
              <td style={styles.td}>
                <Sparkline values={row.sparklineValues} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
